<template>
    <hts-drawer
        v-model="visible"
        color="main"
        :submit="undefined"
        size="large"
        :title="$t('labels.viewHistory')"
        @close="close">
        <div>
            <hts-time-line
                :items="dataHistory"
                is-left-title>
                <template #leftContent="{ item }">
                    <div class="mt-2 p-2 w-32 inline-flex items-center justify-center text-xs font-semibold uppercase h-6 mb-3 sm:mb-0 text-emerald-600">
                        <span class="p-2">
                            {{ $formatters.datetime(item.createdAt) }}
                        </span>
                    </div>
                </template>
                <template #icon="{ item }">
                    <hi-plus-circle-icon
                        v-if="item.action === 'CREATE'"
                        class="h-5 w-5 text-yellow-600 hover:text-yellow-600" />
                    <hi-pencil-square-icon
                        v-else
                        class="h-5 w-5 text-main-600 hover:text-main-500" />
                </template>
                <template #content="{ item }">
                    <div class="flex space-x-2 items-center">
                        <span>
                            {{ labelAction[item.action] }}
                        </span>
                        <hts-avatar
                            v-if="item.action === 'CREATE'"
                            :user="item.createdBy" />
                        <hts-avatar
                            v-if="item.action === 'UPDATE'"
                            :user="item.updatedBy" />
                        <hts-avatar
                            v-if="item.action === 'DELETE'"
                            :user="item.detetedBy" />
                        <span
                            v-if="item.action === 'UPDATE'"
                            class="text-blue-600 cursor-pointer italic"
                            @click="handleOpenPopupViewActivity(item)">
                            {{ $t('labels.viewDetail') }}
                        </span>
                    </div>
                </template>
            </hts-time-line>
            <p
                v-if="isLoad"
                class="text-center">
                Loading ...
            </p>
        </div>
        <hts-modal
            :show="showPopupViewActivity"
            :size="90"
            @close="showPopupViewActivity = false">
            <template #custom>
                <div v-if="module === 'quoteRequest' && Object.keys(itemDetailActivity).length">
                    <quote-request-activity :item-detail-activity="itemDetailActivity" />
                </div>
                <div v-if="module === 'order' && Object.keys(itemDetailActivity).length">
                    <order-activity :item-detail-activity="itemDetailActivity" />
                </div>
            </template>
        </hts-modal>
    </hts-drawer>
</template>

<script lang="ts" setup>
import OrderActivity from './activity-detail/orders.vue'
import QuoteRequestActivity from './activity-detail/quote-request.vue'
import { $lodash } from '#imports'

const visible = ref(false)
const { $formatters } = useNuxtApp()

const props = defineProps({
  module: {
    type: String,
    default: ''
  }
})

const { module } = toRefs(props)

const items = [
  {
    key: 'code',
    icon: 'hi-clock-icon',
    classIcon: 'text-main-600'
  },
  {
    key: 'status'
  }
]

const limitData = 100
const defaultParamRequest = {
  startRow: 0,
  endRow: limitData,
  owner: ''
}
const paramRequest = ref($lodash.cloneDeep(defaultParamRequest))

const dataHistory = ref<Array<{ [key: string]: any }>>([])

const idModule = ref<String>()
const close = () => {
  visible.value = false
  endLoad.value = false
  paramRequest.value = $lodash.cloneDeep(defaultParamRequest)
}
const getData = async () => {
  const api = $urlPartner.activities.get
  try {
    const response = await $api(api, { uriParams: { MODULE: module.value }, body: paramRequest.value })
    const responseData: any = $lodash.get(response, 'data.value')
    if (responseData.success) {
      return $lodash.get(responseData, 'data.rows')
    }
    return []
  } catch (e: any) {
    return []
  }
}

const open = async (id: string) => {
  visible.value = true
  idModule.value = id
  paramRequest.value.owner = id
  dataHistory.value = await getData()

  timeLine.value = document.querySelector('.hts-drawer__body')
  if (timeLine.value) {
    timeLine.value.addEventListener('scroll', handleScroll)
  }
}

const labelAction = {
  CREATE: $t('labels.createdBy'),
  UPDATE: $t('labels.updatedBy1')
}

defineExpose({
  open
})

const showPopupViewActivity = ref(false)

const itemDetailActivity = ref<{ [key: string]: any }>({})
const handleOpenPopupViewActivity = (item: { [key: string]: any }) => {
  itemDetailActivity.value = item
  showPopupViewActivity.value = true
}

const CHeader = computed(() => {
  return defaultHeaderLabel[module.value] || []
})

const defaultHeaderLabel: { [key: string]: any } = {
  driver: [
    {
      key: 'status',
      labels: $t('labels.status'),
      format: (item: number) => {
        return getStatusModule[module.value][item].label || '...'
      }
    },
    {
      key: 'fullName',
      labels: $t('labels.fullName')
    },
    {
      key: 'phone',
      labels: $t('labels.phone'),
      format: (item: { [key: string]: any }) => {
        return `(${item.code}) ${item.phone}`
      }
    },
    {
      key: 'idCardNumber',
      labels: $t('labels.idCardNumber')
    },
    {
      key: 'email',
      labels: $t('labels.email')
    }
  ]
}

const getStatusModule: { [key: string]: any } = {
  vehicle: {
    1: { label: $t('labels.readyShip'), class: 'bg-blue-100 text-blue-800' },
    '-1': { label: $t('labels.vehicleModule.notReady'), class: 'bg-blue-100 text-green-800' }
  },
  driver: {
    0: { label: $t('labels.notReadyYet'), class: 'bg-yellow-100 text-yelow-800' },
    1: { label: $t('labels.conditionallyReady'), class: 'bg-blue-100 text-blue-800' },
    2: { label: $t('labels.ready'), class: 'bg-green-100 text-green-800' }
  },
  order: {
    0: { label: 'pending', class: 'bg-yellow-100 text-yelow-800' },
    1: { label: 'active', class: 'bg-blue-100 text-blue-800' },
    2: { label: 'complete', class: 'bg-green-100 text-green-800' }
  },
  quote: {
    0: { label: $t('labels.init'), class: 'bg-yellow-100 text-yelow-800' },
    1: { label: $t('labels.send'), class: 'bg-blue-100 text-blue-800' },
    2: { label: $t('labels.orderCreated'), class: 'bg-green-100 text-green-800' },
    3: { label: $t('labels.cancel'), class: 'bg-error-100 text-error-800' }
  },
  quoteRequest: {
    0: { label: $t('labels.draft'), class: 'bg-yellow-100 text-yelow-800' },
    1: { label: $t('labels.waitPrice'), class: 'bg-blue-100 text-blue-800' },
    2: { label: $t('labels.quoteCreated'), class: 'bg-green-100 text-green-800' },
    3: { label: $t('labels.cancel'), class: 'bg-error-100 text-error-800' }
  }
}

const pushData = (data: { [key: string]: any }) => {
  dataHistory.value.push(data)
}

const timeLine = ref<Element | null>(null)

const endLoad = ref<boolean>(false)
const isLoad = ref<boolean>(false)
const loadMore = $lodash.debounce(async () => {
  isLoad.value = true
  paramRequest.value.startRow += limitData
  paramRequest.value.endRow += limitData
  const items = await getData()
  if (items.length === 0) {
    endLoad.value = true
  } else {
    items?.forEach((item: { [key: string]: any }) => {
      pushData(item)
    })
  }
  isLoad.value = false
}, 100)

const handleScroll = () => {
  const element = timeLine.value
  if (element && element.scrollTop + element.clientHeight + 50 >= element.scrollHeight) {
    if (!endLoad.value) {
      loadMore()
    }
  }
}

onMounted(() => {})
</script>

<style scoped></style>
